// VIP 12 - Page 2: Prehospital
// 2-1. Cardiac Arrest
// 2-2. Bystander Data
// 2-3. EMS Report - Departure Time
// 2-4. CPR by EMS Personnel at Scene

import React, { useState, useEffect } from "react";

const Page2 = ({ formData, handleChange, handleCheckboxChange, dtDisplay }) => {
  // 디버깅: Page2 로드 시 formData 구조 출력
  useEffect(() => {
    console.log("=== [DEBUG] Page2 formData 구조 ===");
    console.log("전체 formData:", formData);
    console.log("Page2 관련 필드들:", {
      // Cardiac Arrest
      arrest_datetime: formData.arrest_datetime,
      arrest_lnt: formData.arrest_lnt,
      arrest_place: formData.arrest_place,
      baseline_cpc: formData.baseline_cpc,
      arrest_witness: formData.arrest_witness,
      imm_compress: formData.imm_compress,
      // Bystander Data
      bystander_cpr: formData.bystander_cpr,
      bystander_datetime: formData.bystander_datetime,
      bystander_aed: formData.bystander_aed,
      // EMS Report
      ems_call_datetime: formData.ems_call_datetime,
      ems_arrival_datetimetime: formData.ems_arrival_datetimetime,
      X2nd_arrive_time: formData.X2nd_arrive_time,
      EMS_Departure_time: formData.EMS_Departure_time,
      // EMS CPR
      ems_compression: formData.ems_compression,
      ems_compression_method: formData.ems_compression_method,
      ems_initial_rhythm: formData.ems_initial_rhythm,
      ems_initial_rhythm_time: formData.ems_initial_rhythm_time,
      ems_initial_rhythm_obsv: formData.ems_initial_rhythm_obsv,
      ems_defib: formData.ems_defib,
      ems_defib_num: formData.ems_defib_num,
      ems_defib_energy: formData.ems_defib_energy,
      ems_airway: formData.ems_airway,
      ems_airway_datetime: formData.ems_airway_datetime,
      ems_iv: formData.ems_iv,
      ems_iv_fluid: formData.ems_iv_fluid,
      ems_med_epi: formData.ems_med_epi,
      ems_epi_time: formData.ems_epi_time,
      ems_med_amio: formData.ems_med_amio,
      ems_amio_time: formData.ems_amio_time,
      ems_tor: formData.ems_tor,
      ems_tor_reason: formData.ems_tor_reason,
    });

    // 현재 전송 버튼을 누르면 어떻게 전송될지 시뮬레이션
    const filteredFormData = Object.entries(formData).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value) && value.length === 0) return acc;
        if (typeof value === "string" && value.trim() === "") return acc;
        if (value === null || value === undefined) return acc;
        return { ...acc, [key]: value };
      },
      {}
    );

    console.log("Page2에서 전송될 데이터 (빈 값 제외):", filteredFormData);
    console.log("전송 API 정보:", {
      endpoint: "https://product.idea2m.com/api/vip12/save",
      method: "POST",
      payload: {
        formData: filteredFormData,
        projectId: "12",
        caseId: formData.case_id || formData.data_id,
      },
    });
    console.log("=== [DEBUG] Page2 끝 ===");
  }, [formData]);
  // 조건부 필드 표시 상태
  const [showBystanderCprTime, setShowBystanderCprTime] = useState(false);
  const [showEmsCompressionMethod, setShowEmsCompressionMethod] =
    useState(false);
  const [showEmsAirwayTime, setShowEmsAirwayTime] = useState(false);
  const [showEmsIvFluid, setShowEmsIvFluid] = useState(false);
  const [showEmsEpiTime, setShowEmsEpiTime] = useState(false);
  const [showEmsAmioTime, setShowEmsAmioTime] = useState(false);
  const [showEmsTorReason, setShowEmsTorReason] = useState(false);

  // 라디오 버튼 전용 핸들러
  const handleRadioClick = (e) => {
    const { name, value } = e.target;

    // 현재 선택된 값과 클릭된 값이 같으면 해제, 다르면 선택
    if (formData[name] === value) {
      handleChange({ target: { name, value: "" } });
    } else {
      handleChange({ target: { name, value } });
    }

    // 조건부 필드 표시 로직
    handleConditionalFields(name, value);
  };

  // 조건부 필드 표시 처리
  const handleConditionalFields = (name, value) => {
    switch (name) {
      case "bystander_cpr":
        setShowBystanderCprTime(value === "performed");
        break;
      case "ems_compression":
        setShowEmsCompressionMethod(value === "yes");
        break;
      case "ems_airway":
        setShowEmsAirwayTime(value !== "no airway");
        break;
      case "ems_iv":
        setShowEmsIvFluid(value === "yes");
        break;
      case "ems_med_epi":
        setShowEmsEpiTime(value === "yes");
        break;
      case "ems_med_amio":
        setShowEmsAmioTime(value === "yes");
        break;
      case "ems_tor":
        setShowEmsTorReason(value === "yes");
        break;
    }
  };

  // 재사용 가능한 라디오 필드 렌더링 함수
  const renderRadioField = (
    fieldName,
    label,
    options,
    isRequired = false,
    columnType = "2col"
  ) => {
    const wrapperClass =
      columnType === "1col" ? "input-wrapper-1column" : "input-wrapper-2column";
    const blockClass =
      columnType === "1col" ? "block-1column" : "block-2column";
    const titleClass =
      columnType === "1col" ? "item_title_1col" : "item_title_2col";
    const inputClass = columnType === "1col" ? "input_1col" : "input_2col";

    return (
      <div className={wrapperClass}>
        <div className={blockClass}>
          <label className={titleClass}>
            {label}
            {isRequired && <span className="required-mark"> *</span>}
          </label>
          <div className={inputClass}>
            <div className="rad-group">
              {options.map((option) => (
                <label key={option.value} className="rad-label">
                  <input
                    type="radio"
                    className="rad-input"
                    name={fieldName}
                    value={option.value}
                    checked={formData[fieldName] === option.value}
                    onClick={handleRadioClick}
                    onChange={() => {}}
                  />
                  <div className="rad-design"></div>
                  <div className="rad-text">{option.label}</div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 텍스트 입력 필드 렌더링 함수
  const renderTextField = (
    fieldName,
    label,
    placeholder = "",
    unit = "",
    isRequired = false,
    type = "text"
  ) => {
    return (
      <div className="block-2column">
        <label className="item_title_2col">
          {label}
          {isRequired && <span className="required-mark"> *</span>}
        </label>
        <div className="input_2col">
          <input
            type={type}
            name={fieldName}
            placeholder={placeholder}
            value={formData[fieldName] || ""}
            onChange={handleChange}
          />
          {unit && <span className="unit-text">{unit}</span>}
        </div>
      </div>
    );
  };

  return (
    <section className="vip-form-section">
      <h2 className="section-subtitle">Prehospital</h2>

      {/* 2-1. Cardiac Arrest */}
      <fieldset className="basic-info-fieldset">
        <legend>2-1. Cardiac Arrest</legend>

        <div className="input-wrapper-2column">
          {renderTextField(
            "arrest_datetime",
            "Date & time of cardiac arrest",
            "",
            "",
            false,
            "datetime-local"
          )}
          {renderTextField(
            "arrest_lnt",
            "Last normal time",
            "",
            "",
            false,
            "datetime-local"
          )}
        </div>

        {renderRadioField(
          "arrest_place",
          "Location of cardiac arrest",
          [
            { label: "home", value: "home" },
            { label: "school/workplace", value: "school/workplace" },
            { label: "street", value: "street" },
            { label: "public facility", value: "public facility" },
            { label: "nursing facility", value: "nursing facility" },
            { label: "educational facility", value: "educational facility" },
            { label: "sports facility", value: "sports facility" },
            { label: "during EMS transport", value: "during EMS transport" },
            { label: "others", value: "others" },
            { label: "unknown", value: "unknown" },
          ],
          false,
          "1col"
        )}

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">CPC before cardiac arrest</label>
            <div className="input_2col">
              <div className="rad-group">
                {[1, 2, 3, 4, 5].map((num) => (
                  <label key={num} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="baseline_cpc"
                      value={num.toString()}
                      checked={formData.baseline_cpc === num.toString()}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{num}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column">
            <div className="input_2col"></div>
          </div>
        </div>
      </fieldset>

      {/* 2-2. Bystander Data */}
      <fieldset className="basic-info-fieldset">
        <legend>2-2. Bystander Data</legend>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">Arrest witnessed by</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "family", value: "family" },
                  { label: "passerby", value: "passerby" },
                  { label: "EMT", value: "EMT" },
                  {
                    label: "medical personnel (other than EMT)",
                    value: "medical personnel (other than EMT)",
                  },
                  { label: "others", value: "others" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="arrest_witness"
                      value={option.value}
                      checked={formData.arrest_witness === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column">
            <label className="item_title_2col">
              Immediate chest compression
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="imm_compress"
                      value={option.value}
                      checked={formData.imm_compress === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">Bystander CPR</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "performed", value: "performed" },
                  { label: "not performed", value: "not performed" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="bystander_cpr"
                      value={option.value}
                      checked={formData.bystander_cpr === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showBystanderCprTime && (
            <div className="block-2column">
              {renderTextField(
                "bystander_datetime",
                "Bystander CPR start time",
                "",
                "",
                false,
                "datetime-local"
              )}
            </div>
          )}
        </div>

        {renderRadioField(
          "bystander_aed",
          "Bystander AED",
          [
            {
              label: "used but shock not recommended",
              value: "used but shock not recommended",
            },
            {
              label: "used and shock recommended",
              value: "used and shock recommended",
            },
            { label: "not used", value: "not used" },
          ],
          false,
          "1col"
        )}
      </fieldset>

      {/* 2-3. EMS Report - Departure Time */}
      <fieldset className="basic-info-fieldset">
        <legend>2-3. EMS Report - Departure Time</legend>

        <div className="input-wrapper-2column">
          {renderTextField(
            "ems_call_datetime",
            "EMS call date & time",
            "",
            "",
            false,
            "datetime-local"
          )}
          {renderTextField(
            "ems_arrival_datetimetime",
            "EMS arrival time at scene",
            "",
            "",
            false,
            "datetime-local"
          )}
        </div>

        <div className="input-wrapper-2column">
          {renderTextField(
            "X2nd_arrive_time",
            "Time of 2nd Ambulance Arrival",
            "",
            "",
            false,
            "datetime-local"
          )}
          {renderTextField(
            "EMS_Departure_time",
            "Time of EMS Departure from Scene (Time of Departure to Hospital)",
            "",
            "",
            false,
            "datetime-local"
          )}
        </div>
      </fieldset>

      {/* 2-4. CPR by EMS Personnel at Scene */}
      <fieldset className="basic-info-fieldset">
        <legend>2-4. CPR by EMS Personnel at Scene</legend>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">EMS compression</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_compression"
                      value={option.value}
                      checked={formData.ems_compression === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showEmsCompressionMethod && (
            <div className="block-2column">
              <label className="item_title_2col">EMS compression method</label>
              <div className="input_2col">
                <div className="rad-group">
                  {[
                    { label: "manual", value: "manual" },
                    { label: "mechanical", value: "mechanical" },
                  ].map((option) => (
                    <label key={option.value} className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="ems_compression_method"
                        value={option.value}
                        checked={
                          formData.ems_compression_method === option.value
                        }
                        onClick={handleRadioClick}
                        onChange={() => {}}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">{option.label}</div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">EMS initial rhythm</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "asystole", value: "asystole" },
                  { label: "PEA", value: "PEA" },
                  { label: "VF", value: "VF" },
                  { label: "pVT", value: "pVT" },
                  { label: "shockable", value: "shockable" },
                  { label: "unshockable", value: "unshockable" },
                  { label: "unchecked", value: "unchecked" },
                  { label: "unknown", value: "unknown" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_initial_rhythm"
                      value={option.value}
                      checked={formData.ems_initial_rhythm === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {renderTextField(
            "ems_initial_rhythm_time",
            "EMS initial rhythm time",
            "",
            "",
            false,
            "datetime-local"
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              EMS initial rhythm observed by
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "hospital staff", value: "hospital staff" },
                  { label: "ems personnel", value: "ems personnel" },
                  { label: "other", value: "other" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_initial_rhythm_obsv"
                      value={option.value}
                      checked={
                        formData.ems_initial_rhythm_obsv === option.value
                      }
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="block-2column">
            <label className="item_title_2col">EMS defibrillation</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  {
                    label: "AED recommends no shock",
                    value: "AED recommends no shock",
                  },
                  { label: "AED not used", value: "AED not used" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_defib"
                      value={option.value}
                      checked={formData.ems_defib === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="input-wrapper-2column">
          {renderTextField(
            "ems_defib_num",
            "Number of prehospital defibrillation attempts",
            "",
            "times"
          )}
          {renderTextField(
            "ems_defib_energy",
            "Defibrillation energy during 1st attempt",
            "",
            "J"
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">EMS airway</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "SGA", value: "SGA" },
                  { label: "ET", value: "ET" },
                  { label: "others", value: "others" },
                  { label: "no airway", value: "no airway" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_airway"
                      value={option.value}
                      checked={formData.ems_airway === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showEmsAirwayTime && (
            <div className="block-2column">
              {renderTextField(
                "ems_airway_datetime",
                "EMS airway date & time",
                "",
                "",
                false,
                "datetime-local"
              )}
            </div>
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">IV access by EMS</label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_iv"
                      value={option.value}
                      checked={formData.ems_iv === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showEmsIvFluid && (
            <div className="block-2column">
              {renderTextField(
                "ems_iv_fluid",
                "Fluid administered by EMS",
                "",
                "cc"
              )}
            </div>
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Epinephrine administration by EMS
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_med_epi"
                      value={option.value}
                      checked={formData.ems_med_epi === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showEmsEpiTime && (
            <div className="block-2column">
              {renderTextField(
                "ems_epi_time",
                "1st epinephrine administration EMS date & time",
                "",
                "",
                false,
                "datetime-local"
              )}
            </div>
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              Amiodarone administration by EMS
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_med_amio"
                      value={option.value}
                      checked={formData.ems_med_amio === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showEmsAmioTime && (
            <div className="block-2column">
              {renderTextField(
                "ems_amio_time",
                "1st amiodarone administration EMS date & time",
                "",
                "",
                false,
                "datetime-local"
              )}
            </div>
          )}
        </div>

        <div className="input-wrapper-2column">
          <div className="block-2column">
            <label className="item_title_2col">
              CPR discontinuation by EMS personnel
            </label>
            <div className="input_2col">
              <div className="rad-group">
                {[
                  { label: "yes", value: "yes" },
                  { label: "no", value: "no" },
                ].map((option) => (
                  <label key={option.value} className="rad-label">
                    <input
                      type="radio"
                      className="rad-input"
                      name="ems_tor"
                      value={option.value}
                      checked={formData.ems_tor === option.value}
                      onClick={handleRadioClick}
                      onChange={() => {}}
                    />
                    <div className="rad-design"></div>
                    <div className="rad-text">{option.label}</div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          {showEmsTorReason && (
            <div className="block-2column">
              <label className="item_title_2col">
                Reasons for CPR discontinuation
              </label>
              <div className="input_2col">
                <div className="rad-group">
                  {[
                    { label: "rosc", value: "rosc" },
                    {
                      label: "no response to CPR",
                      value: "no response to CPR",
                    },
                    { label: "DNR", value: "DNR" },
                  ].map((option) => (
                    <label key={option.value} className="rad-label">
                      <input
                        type="radio"
                        className="rad-input"
                        name="ems_tor_reason"
                        value={option.value}
                        checked={formData.ems_tor_reason === option.value}
                        onClick={handleRadioClick}
                        onChange={() => {}}
                      />
                      <div className="rad-design"></div>
                      <div className="rad-text">{option.label}</div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </fieldset>
    </section>
  );
};

export default Page2;
